import {
  DOC_CHAT_DEFAULT_MODEL,
  DOC_CHAT_DEFAULT_THINKING_LEVEL,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatModels'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useSyncExternalStore } from 'react'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

export const DOC_CHAT_MODEL_STORAGE_KEY = 'documentSearch_docChatModel:v1'

type DocChatModelSettings = {
  model: string
  thinkingLevel: string
}

const DEFAULT_SETTINGS: DocChatModelSettings = {
  model: DOC_CHAT_DEFAULT_MODEL,
  thinkingLevel: DOC_CHAT_DEFAULT_THINKING_LEVEL,
}

const listeners = new Set<() => void>()

let snapshot: DocChatModelSettings = loadDocChatModelSettings()

function emit(): void {
  listeners.forEach((listener) => listener())
}

function persist(settings: DocChatModelSettings): void {
  try {
    localStorage.setItem(DOC_CHAT_MODEL_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    /* localStorage unavailable */
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): DocChatModelSettings {
  return snapshot
}

function getServerSnapshot(): DocChatModelSettings {
  return DEFAULT_SETTINGS
}

export function loadDocChatModelSettings(): DocChatModelSettings {
  // Persistence is intentionally model-agnostic: the valid models aren't known
  // here, so a stored choice is kept as-is and reconciled against the live
  // backend options later (see useResolvedDocChatModelSettings).
  try {
    const raw = localStorage.getItem(DOC_CHAT_MODEL_STORAGE_KEY)
    if (!raw) return DEFAULT_SETTINGS
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('model' in parsed) ||
      !('thinkingLevel' in parsed) ||
      typeof parsed.model !== 'string' ||
      typeof parsed.thinkingLevel !== 'string' ||
      parsed.model === ''
    ) {
      return DEFAULT_SETTINGS
    }
    return { model: parsed.model, thinkingLevel: parsed.thinkingLevel }
  } catch {
    return DEFAULT_SETTINGS
  }
}

export function setDocChatModel(model: string): void {
  snapshot = { ...snapshot, model }
  persist(snapshot)
  emit()
}

export function setDocChatThinkingLevel(thinkingLevel: string): void {
  snapshot = { ...snapshot, thinkingLevel }
  persist(snapshot)
  emit()
}

export function useDocChatModelSettings(): DocChatModelSettings & {
  isInternal: boolean
  permissionsReady: boolean
  setModel: (model: string) => void
  setThinkingLevel: (thinkingLevel: string) => void
} {
  const settings = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  )
  const { hasPermissions, permissions } = useGeckoPermissions()
  const isInternal = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
  const setModel = useCallback((model: string) => {
    setDocChatModel(model)
  }, [])
  const setThinkingLevel = useCallback((thinkingLevel: string) => {
    setDocChatThinkingLevel(thinkingLevel)
  }, [])

  return {
    ...settings,
    isInternal,
    permissionsReady: permissions !== undefined,
    setModel,
    setThinkingLevel,
  }
}
